import type { MetrikaGoal } from '@anon-chat/shared';

/**
 * Цели и просмотры экранов для Яндекс.Метрики.
 *
 * Визуальный выбор элемента в панели Метрики здесь не работает: страницу нельзя
 * встроить в рамку (защита от кликджекинга), а привязка цели к вёрстке ломалась
 * бы при первой же правке. Поэтому приложение сообщает о событиях само.
 *
 * Номер счётчика нигде не настраивается — он берётся из самого кода счётчика,
 * вставленного администратором. Дублировать его в настройках значило бы завести
 * второе место, где он может разойтись с первым.
 */

interface MetrikaQueue {
  (counterId: number, action: string, ...rest: unknown[]): void;
  /** Очередь вызовов, накопленных до загрузки самого счётчика. */
  a?: unknown[][];
}

function metrika(): MetrikaQueue | null {
  const candidate = (globalThis as { ym?: unknown }).ym;
  return typeof candidate === 'function' ? (candidate as MetrikaQueue) : null;
}

/**
 * Номер счётчика из разметки страницы.
 *
 * Два независимых источника: адрес скрипта счётчика и очередь его вызовов.
 * Первый переживает смену кода инициализации, второй — смену адреса; вместе они
 * закрывают оба случая, а если счётчика нет вовсе, отчёты просто не ведутся.
 */
function detectCounterId(): number | null {
  const script = document.querySelector<HTMLScriptElement>(
    'script[src*="mc.yandex.ru/metrika/tag.js"]',
  );

  if (script) {
    const fromSrc = toCounterId(new URL(script.src, location.href).searchParams.get('id'));
    if (fromSrc) {
      return fromSrc;
    }
  }

  return toCounterId(metrika()?.a?.[0]?.[0]);
}

/**
 * Номер, а не строка: код счётчика инициализирует его числом, и счётчики
 * различаются по нему же. Строка со стороны выглядела бы другим счётчиком, и
 * цели молча не доходили бы.
 */
function toCounterId(value: unknown): number | null {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return null;
  }

  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

let counterId: number | null = null;

/** Вызывается один раз при запуске приложения. */
export function initMetrika(): void {
  counterId = detectCounterId();
}

/**
 * Цель. Вызов до загрузки счётчика не теряется: код счётчика создаёт заглушку,
 * которая складывает вызовы в очередь и отдаёт их, когда счётчик загрузится.
 */
export function reachGoal(goal: MetrikaGoal): void {
  const ym = metrika();
  if (ym && counterId) {
    ym(counterId, 'reachGoal', goal);
  }
}

/**
 * Смена экрана как просмотр страницы.
 *
 * Приложение одностраничное, и без этого Метрика засчитывает один просмотр за
 * визит: ни глубины просмотра, ни времени на экранах, ни воронки по разделам.
 * Адрес синтетический — своего у экрана нет.
 */
export function trackScreen(screen: string, title: string): void {
  const ym = metrika();
  if (!ym || !counterId) {
    return;
  }

  ym(counterId, 'hit', `${location.origin}/${screen}`, {
    title,
    referer: document.referrer,
  });
}
